import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    shipments: [],
    shipments_stats: {},
    shipments_stats_loading: false,
    shipments_filters: DEFAULT_FILTERS,
    shipments_count: 0,
    shipments_loaded: false,
    shipments_loading: false,
    shipments_loading_errors: null,
    fetch_stats: true,
    cancelTokenSource: null,
    cancelTokenSourceStats: null,
    _storageOptions: options || {},
});

const mutations = {
    updateShipments(state, shipments) {
        state.shipments = shipments;
        state.shipments_loaded = true;
        state.shipments_loading = false;
        state.shipments_loading_errors = null;
    },
    updateShipmentsFilters(state, shipments_filters) {
        state.shipments_filters = shipments_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.shipments_filters, fieldsToSave);
        }
    },
    updateShipmentsLoading(state, shipments_loading) {
        state.shipments_loading = shipments_loading;
    },
    updateShipmentsLoadingErrors(state, shipments_loading_errors) {
        state.shipments_loading_errors = shipments_loading_errors;
        state.shipments_loading = false;
    },
    updateShipmentsCount(state, shipments_count) {
        state.shipments_count = shipments_count;
    },
    updateShipmentsStats(state, shipments_stats) {
        state.shipments_stats = shipments_stats;
    },
    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
    updateShipmentsStatsLoading(state, shipments_stats_loading) {
        state.shipments_stats_loading = shipments_stats_loading;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {
    async fetchShipments({ commit, dispatch, state }) {
        if (store.getters['session/current_user_permissions'].indexOf("core.view_shipment") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateShipmentsLoading', true);
        commit('updateShipmentsLoadingErrors', null);
        if (state.fetch_stats) {
            commit('updateShipmentsStatsLoading', true);
        }

        try {
            let filters = { ordering: '-delivery_date', ...(state.shipments_filters || {})};
            const response = await axios.get("/api/shipments/", {
                params: filters,
                cancelToken: source.token,
            });
            commit('updateShipments', response.data.results);
            commit('updateShipmentsCount', response.data.count);
            await dispatch('fetchShipmentsStats');
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateShipmentsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateShipmentsLoading', false);
        }
    },


    async fetchShipmentsStats({ commit, state }) {
        if (!state.fetch_stats) {
            commit('updateShipmentsStatsLoading', false);
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        try {
            commit('updateShipmentsStatsLoading', true);
            const response = await axios.get("/api/shipments/stats/", {
                params: state.shipments_filters,
                cancelToken: source.token,
            });
            commit('updateShipmentsStats', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateShipmentsStatsLoading', false);
        }
    },


    async init({ commit, dispatch, state }, params) {
        commit('updateShipments', []);
        await commit('updateFetchStats', params.fetch_stats === undefined ? true : params.fetch_stats);
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateShipmentsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params.filters || {})
        });
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});
